import api from './axios';
import type { SubscriptionData, SubscriptionTier, SeatInfo } from '../types/platform.types';

export interface InvoiceRecord {
  _id: string;
  invoice_type: 'manual' | 'paystack';
  amount: number;
  currency: string;
  status: 'paid' | 'pending' | 'failed';
  period_start: string;
  period_end: string;
  paid_at?: string;
  manual_bank_reference?: string;
  created_at?: string;
}

export interface InitializePaymentResponse {
  authorization_url: string;
  access_code: string;
  reference: string;
}

export async function getSubscription(): Promise<SubscriptionData> {
  const { data } = await api.get<{ data: SubscriptionData }>('/api/v2/subscription');
  return data.data;
}

export async function getCurrentSubscription(): Promise<SubscriptionData> {
  const { data } = await api.get<{ data: SubscriptionData }>('/api/v2/subscriptions/current');
  return data.data;
}

export async function getSubscriptionPlans(): Promise<any> {
  const { data } = await api.get('/api/v2/subscriptions/plans');
  return data.data;
}

export async function initializeSubscriptionPayment(body: {
  tier: SubscriptionTier;
  billing_cycle: 'monthly' | 'annual';
}): Promise<InitializePaymentResponse> {
  const { data } = await api.post<{ data: InitializePaymentResponse }>(
    '/api/v2/subscriptions/initialize',
    body,
  );
  return data.data;
}

export async function verifySubscriptionPayment(reference: string): Promise<SubscriptionData> {
  const { data } = await api.get<{ data: SubscriptionData }>(
    `/api/v2/subscriptions/verify?reference=${encodeURIComponent(reference)}`,
  );
  return data.data;
}

export async function getInvoices(): Promise<InvoiceRecord[]> {
  const { data } = await api.get<{ data: InvoiceRecord[] }>('/api/v2/subscriptions/invoices');
  return data.data ?? [];
}

export async function getSubscriptionInvoicePdf(invoiceId: string): Promise<string> {
  const response = await api.get(`/api/v2/subscriptions/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
  const blob = new Blob([response.data], { type: 'application/pdf' });
  return URL.createObjectURL(blob);
}

export async function getSeatInfo(): Promise<SeatInfo> {
  const { data } = await api.get<{ data: SeatInfo }>('/api/v2/subscriptions/seats');
  return data.data;
}

export async function cancelAddOn(moduleId: string): Promise<{ message: string; cancel_effective_date: string }> {
  const { data } = await api.delete<{ data: { message: string; cancel_effective_date: string } }>(
    `/api/v2/subscriptions/add-on/${encodeURIComponent(moduleId)}`,
  );
  return data.data;
}

export async function reactivateAddOn(moduleId: string): Promise<{ message: string }> {
  const { data } = await api.post<{ data: { message: string } }>(
    `/api/v2/subscriptions/add-on/${encodeURIComponent(moduleId)}/reactivate`,
  );
  return data.data;
}

export interface InitAddonPaymentResponse {
  authorization_url: string;
  reference: string;
}

export async function initializeAddOnPayment(body: {
  module_id: string;
  billing_cycle: 'monthly' | 'annual';
}): Promise<InitAddonPaymentResponse> {
  const { data } = await api.post<{ data: InitAddonPaymentResponse }>(
    '/api/v2/subscriptions/add-on/initialize',
    body,
  );
  return data.data;
}

export async function verifyAddOnPayment(reference: string): Promise<any> {
  const { data } = await api.get(
    `/api/v2/subscriptions/add-on/verify?reference=${encodeURIComponent(reference)}`,
  );
  return data.data;
}

export async function downgradeSubscription(tier: SubscriptionTier): Promise<void> {
  await api.post('/api/v2/subscriptions/downgrade', { tier });
}

export async function cancelSubscription(): Promise<void> {
  await api.post('/api/v2/subscriptions/cancel');
}

export interface PaymentMethod {
  card: {
    brand: string | null;
    last4: string | null;
    exp_month: string | null;
    exp_year: string | null;
  } | null;
  manage_url: string | null;
}

export async function getPaymentMethod(): Promise<PaymentMethod> {
  const { data } = await api.get<{ data: PaymentMethod }>('/api/v2/subscriptions/payment-method');
  return data.data;
}

export async function recoverSubscription(): Promise<InitializePaymentResponse> {
  const { data } = await api.post<{ data: InitializePaymentResponse }>('/api/v2/subscriptions/recover');
  return data.data;
}

export async function cancelDowngrade(): Promise<{ message: string }> {
  const { data } = await api.post<{ data: { message: string } }>('/api/v2/subscriptions/cancel-downgrade');
  return data.data;
}

export interface UpgradePreviewData {
  current_plan: {
    tier: SubscriptionTier;
    billing_cycle: 'monthly' | 'annual';
    amount: number;
    period_end: string;
  };
  new_plan: {
    tier: SubscriptionTier;
    billing_cycle: 'monthly' | 'annual';
    amount: number;
  };
  proration: {
    days_remaining: number;
    daily_rate_ngn: number;
    credit_ngn: number;
    new_plan_amount: number;
    amount_due_ngn: number;
    credit_covers_pct: number;
  };
  new_period_start: string;
  new_period_end: string;
}

export async function previewUpgrade(body: {
  tier: SubscriptionTier;
  billing_cycle: 'monthly' | 'annual';
}): Promise<UpgradePreviewData> {
  const { data } = await api.post<{ data: UpgradePreviewData }>(
    '/api/v2/subscriptions/upgrade/preview',
    body,
  );
  return data.data;
}

export async function upgradeSubscription(body: {
  tier: SubscriptionTier;
  billing_cycle: 'monthly' | 'annual';
}): Promise<InitializePaymentResponse> {
  const { data } = await api.post<{ data: InitializePaymentResponse }>(
    '/api/v2/subscriptions/upgrade',
    body,
  );
  return data.data;
}
